import React, { useCallback } from 'react';
import styles from './services-card.module.scss';
import {
  Alert,
  DefaultButton,
  DropdownWithSelect,
  ModalWindow,
  RoundTriangle,
  SearchInput,
  CircleImage,
} from 'shared/ui';
import { observer } from 'mobx-react';
import { ServiceMiniCard } from 'shared/ui';
import { CreateService } from 'widgets';
import { ChangeCategories, CreateCategory } from 'features/index';
import { useServicesCard } from '../model';
import { IServicesCard } from 'shared/api/booking/interfaces/services-list.interface';

export const ServicesCard: React.FC = observer(() => {
  const {
    inputValue,
    setInputValue,
    filterMode,
    setFilterMode,
    employeeOptions,
    selectedEmployeeId,
    handleEmployeeChange,
    allEmployees,
    services,
    categories,
    servicesByCategory,
    getUncategorized,
    getEmployeeServices,
    categoriesOpenStatus,
    toggleCategory,
    isUncategorizedOpen,
    setIsUncategorizedOpen,
    employeesOpenStatus,
    toggleEmployee,
    activeModal,
    setActiveModal,
  } = useServicesCard();
  const [clonedServiceData, setClonedServiceData] =
    React.useState<IServicesCard | null>(null);
  const [alertMessage, setAlertMessage] = React.useState<string | null>(null);
  const handleCloseAlert = useCallback(() => setAlertMessage(null), []);

  const normalizedSearch = inputValue.toLowerCase().trim();

  const matchesSearch = (service: IServicesCard): boolean => {
    if (!normalizedSearch) return true;
    return (
      service.name?.toLowerCase().includes(normalizedSearch) ||
      service.category?.category?.toLowerCase().includes(normalizedSearch) ||
      service.employees?.some((e) =>
        e.fio?.toLowerCase().includes(normalizedSearch)
      )
    );
  };

  return (
    <div className={styles.business_services__container}>
      {activeModal === 'createService' && (
        <ModalWindow onClickOutside={() => setActiveModal(null)}>
          <CreateService
            onClose={() => {
              setActiveModal(null);
              setClonedServiceData(null);
            }}
            onSuccess={setAlertMessage}
            initialData={clonedServiceData ?? undefined}
          />
        </ModalWindow>
      )}
      {activeModal === 'createCategory' && (
        <ModalWindow onClickOutside={() => setActiveModal(null)}>
          <CreateCategory
            onClose={() => setActiveModal(null)}
            onSuccess={setAlertMessage}
          />
        </ModalWindow>
      )}
      {activeModal === 'changeCategories' && (
        <ModalWindow onClickOutside={() => setActiveModal(null)}>
          <ChangeCategories
            onClose={() => setActiveModal(null)}
            onSuccess={setAlertMessage}
          />
        </ModalWindow>
      )}
      <div className={styles.container__search}>
        <SearchInput
          value={inputValue}
          placeholder="Поиск по Услугам"
          onChange={setInputValue}
        />
      </div>
      <div className={styles.filter_row}>
        <DefaultButton
          buttonText="По категориям"
          color={filterMode === 'category' ? 'green' : 'white'}
          onClick={() => setFilterMode('category')}
        />
        <DefaultButton
          buttonText="По мастерам"
          color={filterMode === 'employee' ? 'green' : 'white'}
          onClick={() => setFilterMode('employee')}
        />
        {filterMode === 'employee' && (
          <DropdownWithSelect
            options={employeeOptions}
            onChange={handleEmployeeChange}
          />
        )}
      </div>
      <div className={styles.container__content}>
        <div className={styles.content}>
          {filterMode === 'category' &&
            categories.map((category, index) => (
              <div
                className={styles.category__container}
                key={category.id || index}
              >
                <div
                  className={styles.category__title}
                  onClick={() => toggleCategory(index)}
                >
                  <div className={styles.title__name}>{category.category}</div>
                  <RoundTriangle isRotated={!categoriesOpenStatus[index]} />
                </div>
                <div
                  className={`${styles.services__container} ${
                    categoriesOpenStatus[index]
                      ? styles.services__container__open
                      : ''
                  }`}
                >
                  {categoriesOpenStatus[index] &&
                    services
                      .filter(
                        (s) =>
                          s.category?.id === category.id && matchesSearch(s)
                      )
                      .map((service, idx) => (
                        <ServiceMiniCard
                          serviceInfo={service}
                          key={service.id || idx}
                          onDuplicate={(svc) => {
                            setClonedServiceData(svc);
                            setActiveModal('createService');
                          }}
                          onSuccess={setAlertMessage}
                        />
                      ))}
                </div>
              </div>
            ))}
          {filterMode === 'category' &&
            services.filter((s) => !s.category?.id).length > 0 && (
              <div className={styles.category__container}>
                <div
                  className={styles.category__title}
                  onClick={() => setIsUncategorizedOpen(!isUncategorizedOpen)}
                >
                  <div className={styles.title__name}>Без категории</div>
                  <RoundTriangle isRotated={!isUncategorizedOpen} />
                </div>
                <div
                  className={`${styles.services__container} ${
                    isUncategorizedOpen ? styles.services__container__open : ''
                  }`}
                >
                  {isUncategorizedOpen &&
                    services
                      .filter((s) => !s.category?.id && matchesSearch(s))
                      .map((service, idx) => (
                        <ServiceMiniCard
                          serviceInfo={service}
                          key={service.id || idx}
                          onDuplicate={(svc) => {
                            setClonedServiceData(svc);
                            setActiveModal('createService');
                          }}
                          onSuccess={setAlertMessage}
                        />
                      ))}
                </div>
              </div>
            )}
          {filterMode === 'employee' &&
            (selectedEmployeeId
              ? allEmployees.filter((e) => e.id === selectedEmployeeId)
              : allEmployees
            )
              .filter((employee) =>
                getEmployeeServices(employee.id).some(matchesSearch)
              )
              .map((employee) => {
                const employeeServices = getEmployeeServices(
                  employee.id
                ).filter(matchesSearch);
                if (employeeServices.length === 0) return null;

                const categorizedServices =
                  servicesByCategory(employeeServices);
                const uncategorizedServices =
                  getUncategorized(employeeServices);

                return (
                  <div className={styles.employee__container} key={employee.id}>
                    <div
                      className={styles.employee__title}
                      onClick={() => toggleEmployee(employee.id)}
                    >
                      <CircleImage imgSize={32} imgSrc={employee.imgUrl} />
                      <span className={styles.employee__name}>
                        {employee.fio}
                      </span>
                      <RoundTriangle
                        isRotated={employeesOpenStatus[employee.id]}
                      />
                    </div>
                    <div
                      className={`${styles.employee__services__container} ${
                        employeesOpenStatus[employee.id]
                          ? styles.employee__services__container__open
                          : ''
                      }`}
                    >
                      {categorizedServices.map(
                        ({ category: cat, services: catServices }) => (
                          <div
                            className={styles.category__container}
                            key={cat.id}
                          >
                            <div
                              className={styles.category__title}
                              onClick={() =>
                                toggleCategory(categories.indexOf(cat))
                              }
                            >
                              <div className={styles.title__name}>
                                {cat.category}
                              </div>
                              <RoundTriangle
                                isRotated={
                                  categoriesOpenStatus[
                                    categories.indexOf(cat)
                                  ] ?? false
                                }
                              />
                            </div>
                            <div
                              className={`${styles.services__container} ${
                                categoriesOpenStatus[categories.indexOf(cat)]
                                  ? styles.services__container__open
                                  : ''
                              }`}
                            >
                              {categoriesOpenStatus[categories.indexOf(cat)] &&
                                catServices.map((svc, idx) => (
                                  <ServiceMiniCard
                                    serviceInfo={svc}
                                    key={svc.id || idx}
                                    onDuplicate={(svc) => {
                                      setClonedServiceData(svc);
                                      setActiveModal('createService');
                                    }}
                                    onSuccess={setAlertMessage}
                                  />
                                ))}
                            </div>
                          </div>
                        )
                      )}
                      {uncategorizedServices.length > 0 && (
                        <div className={styles.category__container}>
                          <div
                            className={styles.category__title}
                            onClick={() =>
                              setIsUncategorizedOpen(!isUncategorizedOpen)
                            }
                          >
                            <div className={styles.title__name}>
                              Без категории
                            </div>
                            <RoundTriangle isRotated={isUncategorizedOpen} />
                          </div>
                          <div
                            className={`${styles.services__container} ${
                              isUncategorizedOpen
                                ? styles.services__container__open
                                : ''
                            }`}
                          >
                            {isUncategorizedOpen &&
                              uncategorizedServices.map((svc, idx) => (
                                <ServiceMiniCard
                                  serviceInfo={svc}
                                  key={svc.id || idx}
                                  onDuplicate={(svc) => {
                                    setClonedServiceData(svc);
                                    setActiveModal('createService');
                                  }}
                                  onSuccess={setAlertMessage}
                                />
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
        </div>
      </div>
      <div className={styles.container__buttons}>
        <div
          className={styles.button__wrapper}
          onClick={() => {
            setActiveModal('changeCategories');
          }}
        >
          <DefaultButton
            buttonText="Изменить Категории"
            className={styles.button_padding_horizontal}
          />
        </div>
        <div
          className={styles.button__wrapper}
          onClick={() => {
            setActiveModal('createCategory');
          }}
        >
          <DefaultButton
            buttonText="Добавить Категорию"
            className={styles.button_padding_horizontal}
          />
        </div>
        <div
          className={styles.button__wrapper}
          onClick={() => {
            setActiveModal('createService');
          }}
        >
          <DefaultButton
            buttonText="Создать Услугу"
            className={styles.button_padding_horizontal}
          />
        </div>
      </div>
      {alertMessage && (
        <Alert
          type="success"
          message={alertMessage}
          onClose={handleCloseAlert}
        />
      )}
    </div>
  );
});
